import React from 'react';
import { connect } from 'react-redux';

import { bePosTipsRead, bePosTipsWrite } from 'actions/pos/bePosTips.action';
import Toggler from 'components/form/Toggler.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import TRANSFER_POLICY from 'constants/pos/stripeTransferPolicy.const';
import STRIPE_STATUS from 'constants/stripeStatus.const';
import useCallbackUnignored from 'hooks/useCallbackUnignored.hook';
import { usePrepareReadWrite } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import isNilValue from 'utils/predicates/any/isNilValue.util';

import { getSalonOnlineSettings } from 'settings/online-booking/actions';

import CN from './StaffTips.module.scss';

const byIncompleteStylist = (s) => isNilValue(s.stripe_account) && s.stripe_account_status === STRIPE_STATUS.noAccount && s.transfer_policy === TRANSFER_POLICY.noTransfer;

const StaffTips = ({ settingsData }) => {
  const { t } = useTranslation();

  const { qj, qr, mr, up } = usePrepareReadWrite({
    reader: bePosTipsRead,
    writer: bePosTipsWrite,
  });

  const isTipOn = !!qj?.takeTipViaPos;
  const isStaffTipOn = !!qj?.sendTipsToMainStripeAccount;

  const handleToggleTips = useCallbackUnignored(
    async () => {
      await up({
        data: { takeTipViaPos: !isTipOn },
      });
    },
    [isTipOn, up],

    'handleToggleTips()',
  );

  const handleToggleStaffTips = useCallbackUnignored(
    async () => {
      await up({
        data: { sendTipsToMainStripeAccount: !isStaffTipOn },
      });
    },
    [isStaffTipOn, up],

    'handleToggleTips()',
  );
  const incompleteStylistsCount = Object.values(settingsData.stylist_service_data).filter(byIncompleteStylist).length;
  const totalStylistsCount = Object.keys(settingsData?.stylist_service_data).length;
  const shouldShowAlert = totalStylistsCount > 1 || incompleteStylistsCount !== totalStylistsCount;
  const isSpinnerRendered = qr?.isLoading || mr?.isLoading;

  return (
    <div data-bem="StaffTips" className={CN.component}>
      {shouldShowAlert && incompleteStylistsCount > 0 && (
        <AlertPanel tag="StaffTips-alert" variant="danger">
          <div className={CN.alert}>
            <div className={CN.icon}>
              <Icon variant="warning" width="md" />
            </div>
            <div>
              {t('onlineBooking.generalSettings.tips.alertMessagePart1', {
                incomplete: incompleteStylistsCount,
                total: totalStylistsCount,
              })}
              <span className={CN.bold}>{t('onlineBooking.generalSettings.tips.alertMessagePart2')}</span>
              {t('onlineBooking.generalSettings.tips.alertMessagePart3')}
            </div>
          </div>
        </AlertPanel>
      )}

      <div data-bem="StaffTips-tips" className={CN.tipsContainer}>
        <div data-bem="StaffTips-tipBox" className={CN.tipBox}>
          <span className={CN.tipBoxTitle}>{t('onlineBooking.generalSettings.tips.box1Title')}</span>
          <span className={CN.tipBoxText}>{t('onlineBooking.generalSettings.tips.box1Guide')}</span>
          <div className={CN.togglerContainer}>
            <Toggler
              tag="StaffTips__business_stripe_account"
              onClick={handleToggleStaffTips}
              selected={isStaffTipOn}
              onLabel={t('onlineBooking.generalSettings.tips.enabled')}
              offLabel={t('onlineBooking.generalSettings.tips.disabled')}
            />
          </div>
        </div>

        <div data-bem="StaffTips-tipBox" className={CN.tipBox}>
          <span className={CN.tipBoxTitle}>{isTipOn ? t('onlineBooking.generalSettings.tips.box2Title1') : t('onlineBooking.generalSettings.tips.box2Title2')}</span>
          <span className={CN.tipBoxText}>{isTipOn ? t('onlineBooking.generalSettings.tips.box2Guide1') : t('onlineBooking.generalSettings.tips.box2Guide2')}</span>
          <div className={CN.togglerContainer}>
            <Toggler
              tag="StaffTips__tip"
              onClick={handleToggleTips}
              selected={isTipOn}
              onLabel={t('onlineBooking.generalSettings.tips.enabled')}
              offLabel={t('onlineBooking.generalSettings.tips.disabled')}
            />
          </div>
        </div>
      </div>

      {isSpinnerRendered && <OverlaySpinner />}
    </div>
  );
};

const StaffTipsHoc = connect(
  (state) => ({
    settingsData: state.settings.onlineBooking.settingsData,
  }),
  mapActionsToDispatchToProps({
    getSalonOnlineSettings,
  }),
)(StaffTips);

export default StaffTipsHoc;
